// src/pages/SetPassword.jsx
// Opened from the "Set my password" button in the welcome email
// (…/portal/?set-password=<token>). The link works once, for 72 hours.
import { useEffect, useState } from 'react';
import { trackPage } from '../utils/visits';
import { checkSetPasswordLink, setPasswordFromLink } from '../utils/api';
import { pathForTab, DEFAULT_TAB } from '../utils/tabRoutes';
import AuthShell, { AuthMessage, PasswordField } from '../components/AuthShell';
import { passwordProblem, S } from './ChooseOwnPassword';

export function setPasswordTokenFromLocation() {
  return new URLSearchParams(window.location.search).get('set-password');
}

export default function SetPassword({ token }) {
  useEffect(() => { trackPage('set-password'); }, []);   // 3.49.0: Analytics (visitors before signing in)
  // state: 'checking' | 'form' | 'saving' | 'done' | 'problem'
  const [state, setState] = useState('checking');
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [pw, setPw]       = useState('');
  const [again, setAgain] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    checkSetPasswordLink(token)
      .then(r => {
        if (!r.valid) { setMessage(r.message); setState('problem'); return; }
        setEmail(r.email); setName(r.name || ''); setState('form');
      })
      .catch(e => { setMessage(e.message); setState('problem'); });
  }, [token]);

  async function submit(e) {
    e.preventDefault();
    setMessage('');
    const bad = passwordProblem(pw);
    if (bad) { setMessage(bad); return; }
    if (pw !== again) { setMessage("The two passwords don't match."); return; }
    setState('saving');
    try {
      await setPasswordFromLink(token, pw);
      setState('done');
    } catch (e2) { setMessage(e2.message); setState('form'); }
  }

  const goToSignIn = () => window.location.assign(pathForTab(DEFAULT_TAB));

  return (
    <AuthShell stack>
      <h1 style={S.title}>Welcome to the Fax Portal</h1>

      {state === 'checking' && <p style={S.text}>Checking your link…</p>}

      {(state === 'form' || state === 'saving') && (
        <form onSubmit={submit} style={S.form}>
          <p style={S.text}>
            Hello{name ? ` ${name}` : ''}. Choose a password for <strong>{email}</strong>. Next time, sign in with this email and password.
          </p>
          <input type="email" value={email} readOnly hidden autoComplete="username" />
          <label style={S.label}>New password
            <PasswordField value={pw} onChange={e => setPw(e.target.value)} required autoFocus autoComplete="new-password"
              placeholder="At least 8 characters, letters and numbers" />
          </label>
          <label style={S.label}>Type it again
            <PasswordField value={again} onChange={e => setAgain(e.target.value)} required autoComplete="new-password" />
          </label>
          {message && <AuthMessage tone="error">{message}</AuthMessage>}
          <button type="submit" disabled={state === 'saving'} className="auth-btn">{state === 'saving' ? 'Saving…' : 'Save my password'}</button>
        </form>
      )}

      {state === 'done' && (
        <>
          <AuthMessage tone="success">
            Your password is saved. Sign in with <strong>{email}</strong> and your new password. You'll then set up two-factor authentication, which takes about two minutes.
          </AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to sign in</button>
        </>
      )}

      {state === 'problem' && (
        <>
          <AuthMessage tone="error">{message}</AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to the Fax Portal</button>
        </>
      )}
    </AuthShell>
  );
}
